/*
   How a review runs: now, overnight, or whichever the size implies.

   The overnight lane answers within the deployment's ceiling at about half the
   price, and a review past the page threshold takes it without being asked.
   That default is the honest one — most reviews are not urgent — so the choice
   exists for the two cases it does not fit: something due today that happens to
   be long, and something small worth batching anyway.
*/

import { Clock, Sparkles, Zap } from "lucide-react"
import * as React from "react"

import { Skeleton } from "@/components/ui/alert"
import { cn } from "@/lib/utils"

/** Undefined lets the page threshold decide, which is what omitting the field does. */
export type BatchChoice = boolean | undefined

interface Option {
  value: BatchChoice
  label: string
  icon: React.ReactNode
  detail: string
}

export function BatchChoiceField({
  value,
  onChange,
  pageThreshold,
  ceilingHours,
  pages,
}: {
  value: BatchChoice
  onChange: (v: BatchChoice) => void
  pageThreshold: number
  ceilingHours: number
  /** Null while the count is unknown. Never guessed, never shown as a zero. */
  pages: number | null
}): JSX.Element {
  const options: Option[] = [
    {
      value: undefined,
      label: "Automatic",
      icon: <Sparkles className="size-3.5" />,
      detail: `Over ${String(pageThreshold)} pages`,
    },
    {
      value: false,
      label: "Now",
      icon: <Zap className="size-3.5" />,
      detail: "Minutes, full price",
    },
    {
      value: true,
      label: "Overnight",
      icon: <Clock className="size-3.5" />,
      detail: `Within ${String(ceilingHours)}h, half price`,
    },
  ]

  // What Automatic resolves to for this file. Unknown until the count is in,
  // which is why the sentence waits rather than asserting "now".
  const known = pages !== null
  const overnight = value ?? (known && pages > pageThreshold)

  return (
    <div className="mt-5" aria-busy={!known}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-body-sm font-semibold text-text">How to run it</span>
        {known ? (
          <span className="font-id text-caption text-text-3">
            {pages} {pages === 1 ? "page" : "pages"}
          </span>
        ) : (
          <Skeleton className="h-3 w-12" />
        )}
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2">
        {options.map((o) => (
          <button
            key={o.label}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => {
              onChange(o.value)
            }}
            className={cn(
              "rounded-lg border px-3 py-2.5 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
              value === o.value
                ? "border-brand bg-brand-tint/40"
                : "border-border hover:bg-surface-sunken",
            )}
          >
            <span className="flex items-center gap-1.5 text-body-sm font-medium text-text">
              {o.icon}
              {o.label}
            </span>
            <span className="mt-0.5 block text-caption text-text-3">{o.detail}</span>
          </button>
        ))}
      </div>

      {known && (
        <p className="mt-2 max-w-[60ch] text-caption leading-normal text-text-3">
          {overnight ? (
            <>
              This review will run <span className="font-semibold text-text-2">overnight</span> —
              results within {ceilingHours} hours, at about half the cost. Choose Now if it has to
              go out today.
            </>
          ) : (
            <>
              This review will run <span className="font-semibold text-text-2">now</span>, in
              minutes, at full price.
            </>
          )}
        </p>
      )}
    </div>
  )
}
